import { DEX_EXCHANGE_LABELS } from "../dex";

/**
 * All CEX IDs supported by Cede SDK (mapped to CCXT exchange IDs).
 * Includes Hyperliquid for SDK trading features.
 *
 * @note This is slightly different from @cede/types CEDE_CEXS (which excludes Hyperliquid).
 */
export const CEDE_CEXS = {
  BINANCE: "binance",
  COINBASE: "coinbase",
  KRAKEN: "kraken",
  GATEIO: "gateio",
  OKX: "okx",
  BITFINEX: "bitfinex",
  HTX: "huobi",
  KUCOIN: "kucoin",
  BYBIT: "bybit",
  BITGET: "bitget",
  BITSTAMP: "bitstamp",
  BITMART: "bitmart",
  MEXC: "mexc",
  COINBASE_EXCHANGE: "coinbaseexchange",
  CRYPTOCOM: "cryptocom",
  BITHUMB: "bithumb",
  HYPERLIQUID: "hyperliquid",
  TRADEXYZ: "tradexyz",
  PARADEX: "paradex",
  FLX: "flx",
  VNTL: "vntl",
  HYNA: "hyna",
  KM: "km",
  CASH: "cash",
  PARA: "para",
  MKTS: "mkts",
  GRVT: "grvt",
  ZEROONE: "zeroone",
  EXTENDED: "extended",
  HIBACHI: "hibachi",
  DECIBEL: "decibel",
  HOTSTUFF: "hotstuff",
  DANGO: "dango",
  KATANA: "katana",
  RISE: "rise",
  KRAKEN_FUTURES: "krakenfutures",
  LBANK: "lbank",
  PAPER: "paper",
};

export type CEDE_CEXS = (typeof CEDE_CEXS)[keyof typeof CEDE_CEXS];

/**
 * @deprecated Use CEDE_CEXS instead. This is a duplicate.
 */
export const EXCHANGES = {
  BINANCE: "binance",
  COINBASE: "coinbase",
  KRAKEN: "kraken",
  GATEIO: "gateio",
  OKX: "okx",
  BITFINEX: "bitfinex",
  HTX: "huobi",
  KUCOIN: "kucoin",
  BYBIT: "bybit",
  BITGET: "bitget",
  BITSTAMP: "bitstamp",
  BITMART: "bitmart",
  MEXC: "mexc",
  COINBASE_EXCHANGE: "coinbaseexchange",
  CRYPTOCOM: "cryptocom",
  BITHUMB: "bithumb",
  HYPERLIQUID: "hyperliquid",
  TRADEXYZ: "tradexyz",
  PARADEX: "paradex",
  FLX: "flx",
  VNTL: "vntl",
  HYNA: "hyna",
  KM: "km",
  CASH: "cash",
  PARA: "para",
  MKTS: "mkts",
  GRVT: "grvt",
  ZEROONE: "zeroone",
  EXTENDED: "extended",
  HIBACHI: "hibachi",
  DECIBEL: "decibel",
  HOTSTUFF: "hotstuff",
  KATANA: "katana",
  RISE: "rise",
  KRAKEN_FUTURES: "krakenfutures",
  LBANK: "lbank",
  POLYMARKET: "polymarket",
  // Onchain DEX - Format: {protocol}|{chain}
  AERODROME_CL_BASE: "aerodrome-cl|base",
  AERODROME_CL_2_BASE: "aerodrome-cl-2|base",
  PANCAKE_V3_ARBITRUM: "pancake-v3|arbitrum",
  PANCAKE_V3_BASE: "pancake-v3-base|base",
  PANCAKE_INFINITY_BSC: "pancake-infinity-bsc|bsc",
  UNISWAP_V4_ROBINHOOD: "uniswap-v4-robinhood|robinhood",
  // Solana DEXes
  METEORA_SOLANA: "meteora|solana",
  METEORA_DYN_SOLANA: "meteora-dyn|solana",
  METEORA_DYN2_SOLANA: "meteora-dyn2|solana",
  METEORA_DBC_SOLANA: "meteora-dbc|solana",
  RAYDIUM_SOLANA: "raydium|solana",
  RAYDIUM_CLMM_SOLANA: "raydium-clmm|solana",
  RAYDIUM_CPMM_SOLANA: "raydium-cpmm|solana",
  RAYDIUM_LAUNCHLAB_SOLANA: "raydium-launchlab|solana",
  ORCA_SOLANA: "orca|solana",
  PUMPSWAP_SOLANA: "pumpswap|solana",
  JUPITER_STUDIO_SOLANA: "jupiter-studio|solana",
} as const;

export type EXCHANGES = (typeof EXCHANGES)[keyof typeof EXCHANGES];

/**
 * Exchanges supported for SDK token-selling feature.
 * Only includes highly liquid CEX with reliable market orders.
 *
 * @note This is DIFFERENT from @cede/types SUPPORTED_EXCHANGES which has all Hub exchanges.
 * @note Only 6 exchanges for token-selling to ensure reliable execution.
 */
export const SUPPORTED_EXCHANGES = {
  KUCOIN: CEDE_CEXS.KUCOIN,
  MEXC: CEDE_CEXS.MEXC,
  BINANCE: CEDE_CEXS.BINANCE,
  BYBIT: CEDE_CEXS.BYBIT,
  GATEIO: CEDE_CEXS.GATEIO,
  BITGET: CEDE_CEXS.BITGET,
} as const;
export type SUPPORTED_EXCHANGES = (typeof SUPPORTED_EXCHANGES)[keyof typeof SUPPORTED_EXCHANGES];

/** CEX that will be supported soon (promoted on landing pages) */
export const CEDE_CEXS_SOON_SUPPORTED = {
  GEMINI: "gemini",
  BINANCE_US: "binanceus",
};

export const getCCXTExchangeId = (internalCexId: string) => {
  switch (internalCexId) {
    case CEDE_CEXS.BITFINEX: {
      return "bitfinex2";
    }
    case CEDE_CEXS.HTX: {
      return "htx";
    }
    default: {
      return internalCexId;
    }
  }
};

export const getInternalExchangeId = (ccxtExchangeId: string) => {
  switch (ccxtExchangeId) {
    case "bitfinex2": {
      return CEDE_CEXS.BITFINEX;
    }
    case "htx": {
      return CEDE_CEXS.HTX;
    }
    default: {
      return ccxtExchangeId;
    }
  }
};

/**
 * CEX labels - used by SDK for centralized exchange display names
 */
export const CEDE_CEX_LABELS: Record<string, string> = {
  [CEDE_CEXS.BINANCE]: "Binance",
  [CEDE_CEXS.COINBASE]: "Coinbase",
  [CEDE_CEXS.COINBASE_EXCHANGE]: "Coinbase Exchange",
  [CEDE_CEXS.KRAKEN]: "Kraken",
  [CEDE_CEXS.GATEIO]: "Gate.io",
  [CEDE_CEXS.OKX]: "OKX",
  [CEDE_CEXS.BITFINEX]: "Bitfinex",
  [CEDE_CEXS.HTX]: "HTX",
  [CEDE_CEXS.KUCOIN]: "KuCoin",
  [CEDE_CEXS.BYBIT]: "Bybit",
  [CEDE_CEXS.BITGET]: "Bitget",
  [CEDE_CEXS.BITSTAMP]: "Bitstamp",
  [CEDE_CEXS.BITMART]: "Bitmart",
  [CEDE_CEXS.MEXC]: "MEXC",
  [CEDE_CEXS.CRYPTOCOM]: "Crypto.com",
  [CEDE_CEXS.BITHUMB]: "Bithumb",
  [CEDE_CEXS.HYPERLIQUID]: "Hyperliquid",
  [CEDE_CEXS.TRADEXYZ]: "TradeXYZ",
  [CEDE_CEXS.PARADEX]: "Paradex",
  [CEDE_CEXS.FLX]: "Felix Exchange",
  [CEDE_CEXS.VNTL]: "Ventuals",
  [CEDE_CEXS.HYNA]: "HyENA",
  [CEDE_CEXS.KM]: "Markets by Kinetiq",
  [CEDE_CEXS.CASH]: "Dream Cash",
  [CEDE_CEXS.PARA]: "Para",
  // distinct from `km` (same deployer) so they don't render identically
  [CEDE_CEXS.MKTS]: "Markets by Kinetiq (mkts)",
  [CEDE_CEXS.GRVT]: "GRVT",
  [CEDE_CEXS.ZEROONE]: "01.xyz",
  [CEDE_CEXS.EXTENDED]: "Extended",
  [CEDE_CEXS.HIBACHI]: "Hibachi",
  [CEDE_CEXS.DECIBEL]: "Decibel",
  [CEDE_CEXS.HOTSTUFF]: "Hotstuff",
  [CEDE_CEXS.DANGO]: "Dango",
  [CEDE_CEXS.KATANA]: "Katana",
  [CEDE_CEXS.RISE]: "Rise",
  [CEDE_CEXS.KRAKEN_FUTURES]: "Kraken Futures",
  [CEDE_CEXS.LBANK]: "LBank",
  [CEDE_CEXS.PAPER]: "Paper Exchange",
  // not supported yet, but promoted on our landing
  [CEDE_CEXS_SOON_SUPPORTED.GEMINI]: "Gemini",
  [CEDE_CEXS_SOON_SUPPORTED.BINANCE_US]: "Binance US",
};

/**
 * DEX labels - used for decentralized exchange display names
 * Format: {protocol}|{chain} -> "Protocol Name (Chain)"
 *
 * Supported DEXs are auto-derived from DEX_CONFIG in ../dex/constants.ts
 * Future/unsupported DEXs are listed here for completeness
 */
export const DEX_LABELS: Record<string, string> = {
  // Supported DEXs (from centralized config)
  ...DEX_EXCHANGE_LABELS,
  // Future/unsupported DEXs (for display purposes)
  "uniswap|ethereum": "Uniswap (Ethereum)",
  "uniswap-v3|ethereum": "Uniswap V3 (Ethereum)",
  "uniswap-v3|arbitrum": "Uniswap V3 (Arbitrum)",
  "uniswap-v3|polygon": "Uniswap V3 (Polygon)",
  "uniswap-v3|base": "Uniswap V3 (Base)",
  "pancakeswap|bsc": "PancakeSwap (BSC)",
  "raydium|solana": "Raydium (Solana)",
};

/**
 * Combined exchange labels - includes both CEXs and DEXs
 * Use this for UI display when you need to handle both types
 */
export const EXCHANGE_LABELS: Record<string, string> = {
  ...CEDE_CEX_LABELS,
  ...DEX_LABELS,
};

export const CEDE_CEX_SIGN_UP_URLS = {
  [CEDE_CEXS.BINANCE]: "https://accounts.binance.com/register?ref=717717563",
  [CEDE_CEXS.COINBASE]: "https://login.coinbase.com/signup",
  [CEDE_CEXS.COINBASE_EXCHANGE]: "https://login.coinbase.com/signup",
  [CEDE_CEXS.KRAKEN]: "https://invite.kraken.com/JDNW/4wo630pe",
  [CEDE_CEXS.KRAKEN_FUTURES]: "https://invite.kraken.com/JDNW/4wo630pe",
  [CEDE_CEXS.GATEIO]: "https://www.gate.io/signup/A1BAAw1e?ref_type=103",
  [CEDE_CEXS.OKX]: "https://www.okx.com/join/37788484",
  [CEDE_CEXS.BITFINEX]: "https://www.bitfinex.com/sign-up?refcode=bDH0KF0rN",
  [CEDE_CEXS.HTX]: "https://www.huobi.com/invite/fr-fr/1f?invite_code=k88z7223",
  [CEDE_CEXS.KUCOIN]: "https://www.kucoin.com/r/af/QBS5U5CX",
  [CEDE_CEXS.BYBIT]: "https://partner.bybit.com/b/61283",
  [CEDE_CEXS.BITGET]: "https://partner.bitget.com/bg/0WH9QH",
  [CEDE_CEXS.BITSTAMP]: "https://www.bitstamp.net/onboarding/register/account-holder/",
  [CEDE_CEXS.BITMART]: "https://www.bitmart.com/invite/c7Gsdg",
  [CEDE_CEXS.MEXC]: "https://promote.mexc.com/r/A1Zw0YYS",
  [CEDE_CEXS.CRYPTOCOM]: "https://crypto.com/exch/cede",
  [CEDE_CEXS.BITHUMB]: "https://www.bithumb.com/react/member/join",
  [CEDE_CEXS.HYPERLIQUID]: "https://app.hyperliquid.xyz/join/CEDEHUB",
  [CEDE_CEXS.GRVT]: "https://grvt.io/?ref=CedeFlow",
  [CEDE_CEXS.HIBACHI]: "https://hibachi.xyz/r/cedeflow",
  [CEDE_CEXS.LBANK]: "https://www.lbank.com/signup/a?icode=5X7JK",
};

/**
 * HIP-3 platform exchange IDs.
 * All HIP-3 platforms use the same Hyperliquid API with a `dex` parameter.
 */
// Actively-farmed HIP-3 sub-DEXes. hyna/cash/flx/vntl/km/mkts are dormant — every one of
// their markets is `isDelisted` — and para isn't SDK-streamable, so they're excluded
// from the pipelines that key off this list.
export const HIP3_EXCHANGE_IDS = [CEDE_CEXS.TRADEXYZ] as const;

/**
 * Settlement/collateral coin for each HIP-3 platform.
 * Different HIP-3 platforms settle trades in different stablecoins.
 * Native Hyperliquid uses USDC.
 */
export const HIP3_SETTLEMENT_COINS: Record<(typeof HIP3_EXCHANGE_IDS)[number], string> = {
  [CEDE_CEXS.TRADEXYZ]: "USDC",
};

/**
 * Get the settlement coin for a Hyperliquid-compatible exchange.
 * Returns "USDC" for native Hyperliquid and the platform-specific coin for HIP-3 exchanges.
 */
export function getHyperliquidSettlementCoin(exchangeId: string): string {
  if (isHip3Exchange(exchangeId)) {
    return HIP3_SETTLEMENT_COINS[exchangeId as (typeof HIP3_EXCHANGE_IDS)[number]];
  }
  return "USDC";
}

/**
 * Exchanges that use EVM addresses (Ethereum-compatible addresses starting with 0x)
 * These exchanges require the user's defiAddress to query orders and trades
 */
export const EVM_BASED_EXCHANGES: ReadonlySet<CEDE_CEXS> = new Set([
  CEDE_CEXS.HYPERLIQUID,
  ...HIP3_EXCHANGE_IDS,
  CEDE_CEXS.PARADEX,
  CEDE_CEXS.GRVT,
  CEDE_CEXS.EXTENDED,
  CEDE_CEXS.HIBACHI,
  CEDE_CEXS.HOTSTUFF,
  CEDE_CEXS.KATANA,
  CEDE_CEXS.RISE,
]);

/**
 * Check if an exchange is a HIP-3 platform.
 */
export function isHip3Exchange(exchangeId: string): boolean {
  return HIP3_EXCHANGE_IDS.includes(exchangeId as (typeof HIP3_EXCHANGE_IDS)[number]);
}

/**
 * Check if an exchange is Hyperliquid-compatible (Hyperliquid or any HIP-3 platform).
 * These exchanges share the same API and authentication mechanism.
 */
export function isHyperliquidCompatible(exchangeId: string): boolean {
  return exchangeId === CEDE_CEXS.HYPERLIQUID || isHip3Exchange(exchangeId);
}

/**
 * Check if an exchange is EVM-based (uses Ethereum-compatible addresses)
 * @param exchangeId - The exchange ID to check
 * @returns true if the exchange is EVM-based
 */
export const isEvmBasedExchange = (exchangeId: string): boolean => {
  return EVM_BASED_EXCHANGES.has(exchangeId as CEDE_CEXS);
};

/**
 * Check if an exchange ID is a DEX (decentralized exchange)
 * DEX IDs use format: {protocol}|{chain} (e.g., "aerodrome-cl|base", "pancake-v3|arbitrum")
 * @param exchangeId - The exchange ID to check
 * @returns true if the exchange is a DEX
 */
export const isDexId = (exchangeId: string): boolean => exchangeId.includes("|");

/**
 * Check if an exchange ID is a CEX (centralized exchange)
 * @param exchangeId - The exchange ID to check
 * @returns true if the exchange is a CEX
 */
export const isCexId = (exchangeId: string): boolean => !isDexId(exchangeId);

/**
 * Exchanges that support perpetual (perps) markets in addition to spot.
 * These exchanges require marketType configuration in campaign epochs.
 * Add new perps exchanges here as they are integrated.
 */
export const EXCHANGES_WITH_PERPS: ReadonlySet<CEDE_CEXS> = new Set([
  CEDE_CEXS.HYPERLIQUID,
  ...HIP3_EXCHANGE_IDS,
  CEDE_CEXS.BINANCE,
  CEDE_CEXS.PARADEX,
  CEDE_CEXS.GRVT,
  CEDE_CEXS.ZEROONE,
  CEDE_CEXS.EXTENDED,
  CEDE_CEXS.HIBACHI,
  CEDE_CEXS.DECIBEL,
  CEDE_CEXS.HOTSTUFF,
  CEDE_CEXS.KATANA,
  CEDE_CEXS.RISE,
  CEDE_CEXS.LBANK,
]);

/**
 * Exchanges that use Solana addresses (Ed25519 keypairs, base58 addresses)
 * These exchanges require Solana wallet connection instead of EVM wallets.
 */
export const SOLANA_BASED_EXCHANGES: ReadonlySet<CEDE_CEXS> = new Set([CEDE_CEXS.ZEROONE]);

/**
 * Check if an exchange is Solana-based (uses Solana addresses)
 * @param exchangeId - The exchange ID to check
 * @returns true if the exchange is Solana-based
 */
export const isSolanaBasedExchange = (exchangeId: string): boolean => {
  return SOLANA_BASED_EXCHANGES.has(exchangeId as CEDE_CEXS);
};

/**
 * Exchanges that ONLY support perpetual markets (no spot trading).
 * For these exchanges, all trades are perps regardless of pair symbol.
 */
export const PERPS_ONLY_EXCHANGES: ReadonlySet<CEDE_CEXS> = new Set([
  CEDE_CEXS.KRAKEN_FUTURES,
  CEDE_CEXS.KATANA,
  CEDE_CEXS.LBANK,
]);

/**
 * Check if an exchange only offers futures/perps markets (no spot).
 * Used to default market-pair queries to futures wallet types instead of spot.
 */
export function isFutureOnlyExchange(exchangeId: string): boolean {
  return PERPS_ONLY_EXCHANGES.has(exchangeId as CEDE_CEXS);
}

/**
 * Check if an exchange supports perpetual markets
 * @param exchangeId - The exchange ID to check
 * @returns true if the exchange supports perps
 */
export const exchangeSupportsPerps = (exchangeId: string): boolean => {
  return EXCHANGES_WITH_PERPS.has(exchangeId as CEDE_CEXS);
};

/**
 * Cede's outbound (egress) IP addresses — the NAT/proxy IPs that exchange API
 * requests originate from. Exchanges that require IP-whitelisting (Kraken / Crypto.com
 * OAuth, LBank broker key whitelist) must whitelist exactly these. SINGLE SOURCE OF
 * TRUTH: keep in sync with the prod egress config and the per-exchange API-key
 * tutorials (e.g. apps/hub-v2/public/tutorials/api-keys/lbank/lbank.md). If this drifts
 * from the real egress set, whitelisted private calls fail with IP-permission errors.
 */
export const CEDE_EGRESS_IP_WHITELIST: readonly string[] = [
  "52.214.234.117",
  "54.155.114.62",
  "52.31.181.93",
  "34.249.39.132",
  "46.137.18.177",
  "34.251.214.210",
  "52.210.146.50",
];
